/**
 * Utility functions for tab configuration and filtering logic
 * 
 * This module centralizes the logic for:
 * - Tab index calculation based on user persona (admin/customer/committee) and role (PI/RA)
 * - Status filtering for IRB and RA tabs using progress.status property
 * - Date range filtering with support for multiple date formats
 * 
 * Used by: FiltersPanel.js, list.js (ReviewsListCards), Approvals.js (Admin), My_Requests/list.js (Customer), reviews_list.js (Committee)
 */

/**
 * Get tab configuration based on persona and user type
 * @param {string} persona - "admin", "customer", or "committee"
 * @param {boolean} isCoreValidator - whether user is core validator (for admin persona)
 * @returns {object} - { showStatusFilters, validatorTabs }
 */
export const getTabConfig = (persona, isCoreValidator = false) => {
  if (persona === "admin") {
    // Admin tabs: Assigned (0), Sent Back (1), In Progress (2), Approved (3), Rejected (4), Assessment Reviews (5 - core validator only)
    const validatorTabs = [2, 4]; // In Progress and Rejected tabs (excluding Approved tab 3)
    return { validatorTabs };
  }
  
  if (persona === "customer") {
    // Customer tabs: Assigned (0), Submitted (1), Approved (2), Rejected (3)
    const validatorTabs = [1, 3]; // Submitted and Rejected tabs (excluding Approved tab 2)
    return { validatorTabs };
  }
  
  if (persona === "committee") {
    // Assessment Reviewer tabs: Pending (0), Approved (1), Rejected (2), Pricing (3), Committee (4)
    const validatorTabs = [3, 4]; // Pricing and Committee tabs
    return { validatorTabs };
  }
  
  // For other personas, no status filters
  return { validatorTabs: [] };
};

/**
 * Check if status filters should be shown for current tab
 * @param {string} persona - "admin", "customer", or "committee"
 * @param {number} currentTab - current active tab index
 * @param {boolean} isCoreValidator - whether user is core validator (for admin persona)
 * @returns {boolean} - true if status filters should be shown
 */
export const shouldShowStatusFilters = (persona, currentTab, isCoreValidator = false) => {
  if (persona !== "admin" && persona !== "customer" && persona !== "committee") {
    return false;
  }
  
  const { validatorTabs } = getTabConfig(persona, isCoreValidator);
  return validatorTabs.includes(currentTab);
};

/**
 * Get filtered data based on status filter for validator tabs
 * @param {string} persona - "admin", "customer", or "committee"
 * @param {number} currentTab - current active tab index
 * @param {string} statusFilter - "all", "technical", "business", "core", "pricing", or "ra"
 * @param {boolean} isCoreValidator - whether user is core validator (for admin persona)
 * @param {array} defaultData - data array to filter
 * @returns {array} - filtered data array
 */
export const getFilteredStatusData = (persona, currentTab, statusFilter, isCoreValidator, defaultData) => {
  console.log("filtering");
  console.log("status-filter ", statusFilter);
  
  
  if (statusFilter === "all") {
    return defaultData;
  }
  
  const { validatorTabs } = getTabConfig(persona, isCoreValidator);
  
  // Filter by progress.status for validator tabs
  if (validatorTabs.includes(currentTab)) {
    return defaultData.filter(progress => {
      const status = progress.status?.toLowerCase() || '';
      
      
      if (persona === "committee") {
        // For committee, check for specific status patterns
        switch (statusFilter) {
          case "approved":
            return status.includes("approved");
          case "rejected":
            return status.includes("rejected");
          case "in-progress":
            return status.includes("active");
          default:
            return true; // fallback for "all"
        }
      } else if (status.includes("submitted") && !status.includes("rejected") &&persona == "customer") {

        switch (statusFilter) {
          case "technical":
            return status=="submitted";
          case "business":
            return status.includes("technical");
          case "core":
            return status.includes("business");
          case "pricing":
            return status.includes("core");
          case "ra":
            return status.includes("pricing");
          default:
            return true; // fallback for "all"
        }
      } else {
        // For admin and customer personas
        console.log("for admin and customer filter");
        
        switch (statusFilter) {
          case "technical":
            return status.includes("technical");
          case "business":
            return status.includes("business");
          case "core":
            return status.includes("core");
          case "pricing":
            return status.includes("pricing");
          case "ra":
            return status.includes("ra");
          default:
            return true; // fallback for "all"
        }
      }
    });
  }
  
  return defaultData;
};

/**
 * Parse date range from different possible formats
 * @param {any} dateRange - date range in various possible formats
 * @returns {object} - { startDate, endDate }
 */
const parseDateRange = (dateRange) => {
  let startDate = null;
  let endDate = null;
  
  if (Array.isArray(dateRange)) {
    [startDate, endDate] = dateRange;
  } else if (dateRange.startDate && dateRange.endDate) {
    ({ startDate, endDate } = dateRange);
  } else if (dateRange.start && dateRange.end) {
    startDate = dateRange.start;
    endDate = dateRange.end;
  } else if (dateRange.from && dateRange.to) {
    startDate = dateRange.from;
    endDate = dateRange.to;
  }
  
  return { startDate, endDate };
};

/**
 * Check if a date falls within the given date range
 * @param {Date} date - date to check
 * @param {any} startDate - start date (can be Moment object or Date)
 * @param {any} endDate - end date (can be Moment object or Date)
 * @returns {boolean} - true if date is in range
 */
const checkDateInRange = (date, startDate, endDate) => {
  let inRange = true;
  
  if (startDate) {
    // Handle Moment objects - convert to Date or use Moment methods
    const fromDate = startDate._isAMomentObject ? startDate.toDate() : new Date(startDate);
    fromDate.setHours(0, 0, 0, 0); // Start of day
    inRange = inRange && date >= fromDate;
  }
  if (endDate) {
    // Handle Moment objects - convert to Date or use Moment methods
    const toDate = endDate._isAMomentObject ? endDate.toDate() : new Date(endDate);
    toDate.setHours(23, 59, 59, 999); // End of day
    inRange = inRange && date <= toDate;
  }
  
  return inRange;
};

/**
 * Check if a request progress matches the date range filter
 * @param {object} progress - request progress object
 * @param {any} dateRange - date range filter
 * @returns {boolean} - true if matches date filter
 */
export const matchesDateFilter = (progress, dateRange) => {
  if (!dateRange) {
    return true;
  }
  
  const submissionDate = new Date(progress.request.createdAt);
  const lastActionDate = new Date(progress.updatedAt);
  
  const { startDate, endDate } = parseDateRange(dateRange);
  
  // Only apply date filter if we have at least one date
  if (startDate || endDate) {
    return checkDateInRange(submissionDate, startDate, endDate) || 
           checkDateInRange(lastActionDate, startDate, endDate);
  }
  
  return true;
};
